import * as React from "react";
import { useCallback } from "react";
import styled from "@mui/material/styles/styled";
import Dialog from "@mui/material/Dialog/Dialog";
import { Button, DialogActions, DialogContent } from "@mui/material";
import { HistoryAndPhysical } from "../historyAndPhysical";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import IconButton from "@mui/material/IconButton";
import CancelIcon from "@mui/icons-material/Cancel";
import { useDiagnosticNavigation } from "../useDiagnosticNavigation";
import { MobileView } from "../../../util/aKaunGeneralUtil";
import { usePatientCase } from "../usePatientCase";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { useScreenSize } from "../util/useScreenSize";

//region [[ Styles ]]

const ImproveDiagnosisPopupView = styled("div")(({ theme }) => ({}));

const ActionsStyled = styled(DialogActions)(({ theme }) => ({
  display: "flex",
  boxShadow: "0px 0px 17px rgba(0, 0, 0, 0.08)",
  flexDirection: "row-reverse",
  justifyContent: "center",
  alignItems: "center",
  paddingTop: "20px",
  paddingBottom: "20px",
  paddingLeft: theme.spacing(4),
  paddingRight: theme.spacing(4),
  [theme.breakpoints.down(MobileView)]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  textTransform: "capitalize",
  boxShadow: "none",
  fontSize: "16px",
  width: "360px",

  borderRadius: "25px",
  backgroundColor: KahunColorMap.Primary,
  "&:hover": {
    backgroundColor: KahunColorMap.Primary,
    boxShadow: "none",
  },

  color: "white",
}));

const HeaderContainer = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "60px",
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(4),
  background: KahunColorMap.Secondary,
  boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.08)",
  position: "relative",

  [theme.breakpoints.down(MobileView)]: {
    paddingLeft: theme.spacing(0),
    paddingRight: theme.spacing(2),
  },
}));

const PopupTitle = styled("div")(({ theme }) => ({
  fontSize: "18px",
  fontWeight: "bold",
  color: "white",
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  right: "25px",
  top: "calc(50% - 25px)",
}));
const CloseIcon = styled(CancelIcon)(({ theme }) => ({
  color: "white",
  fontSize: "24px",
}));

const DialogContentStyled = styled(DialogContent)(({ theme }) => ({
  maxHeight: "600px",

  [theme.breakpoints.up(MobileView)]: {
    paddingLeft: "44px",
    paddingRight: "44px",
  },
}));

const ImproveDiagnosisDialog = styled(Dialog)({
  "& .MuiDialog-paper": {
    margin: "25px",
    boxShadow: "0px 0px 17px rgba(0, 0, 0, 0.08)",
    borderRadius: "25px",
    width: "721px",
  },
});

//endregion [[ Styles ]]

//region [[ Props ]]

export interface ImproveDiagnosisPopupProps {}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]] // //

export const ImproveDiagnosisPopup = ({
  ...props
}: ImproveDiagnosisPopupProps) => {
  const {
    improveDiagnosisPopupOpen,
    setImproveDiagnosisPopupOpen,
  } = useDiagnosticNavigation();

  const { isMobile } = useScreenSize();

  const {
    isPendingChange,
    applyPendingFindings,
    clearPendingPlanChanges,
  } = usePatientCase();

  const handleDone = (e) => {
    evt({
      category: CATEGORY.IMPROVE_DIAGNOSIS,
      action: ACTION.DONE_IMPROVE_DIAGNOSIS,
    });

    if (!isMobile) {
      applyPendingFindings();
    }
    setImproveDiagnosisPopupOpen(false);
  };

  const handleCancel = useCallback(() => {
    clearPendingPlanChanges();
    setImproveDiagnosisPopupOpen(false);
  }, [setImproveDiagnosisPopupOpen, clearPendingPlanChanges]);

  return (
    <ImproveDiagnosisPopupView>
      <ImproveDiagnosisDialog
        open={improveDiagnosisPopupOpen}
        onClose={handleCancel}
      >
        <HeaderContainer>
          <PopupTitle>Improve Suggestions</PopupTitle>
          <CloseButton onClick={handleCancel}>
            <CloseIcon />
          </CloseButton>
        </HeaderContainer>
        <DialogContentStyled>
          <HistoryAndPhysical />
        </DialogContentStyled>
        <ActionsStyled>
          <ActionButton
            onClick={handleDone}
            color={isPendingChange() ? "primary" : "inherit"}
            disabled={!isPendingChange()}
            variant={"contained"}
          >
            Update
          </ActionButton>
        </ActionsStyled>
      </ImproveDiagnosisDialog>
    </ImproveDiagnosisPopupView>
  );
};
